import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Upload, FileText, ImageIcon, Sparkles, ArrowRight, ArrowLeft, AlertCircle, CheckCircle, Loader2, Package, MapPin, Phone, Globe, Star } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '@/components/Logo';
import Cookies from 'js-cookie';
import { getApiUrl } from '@/config/api';
import { frictionlessTempApi } from '@/services/api/frictionlessTempApi';
import { useToast } from '@/components/ui/use-toast';
import { MAX_UPLOAD_SIZE_MB, MAX_UPLOAD_SIZE_BYTES, ALLOWED_FILE_TYPES } from '@/config/upload';
import { Card } from '@/components/ui/card';
const RestaurantMenuUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasPendingExtraction, setHasPendingExtraction] = useState(false);
  const [extractedItemsCount, setExtractedItemsCount] = useState<number>(0);
  const [isClaiming, setIsClaiming] = useState(false);
  const [duplicateMenuFound, setDuplicateMenuFound] = useState<any>(null);
  const [isCopyingMenu, setIsCopyingMenu] = useState(false);
  
  // Check if uploading for a new location or special menu
  const searchParams = new URLSearchParams(window.location.search);
  const isNewLocation = searchParams.get('new_location') === 'true';
  const seasonalMenuId = searchParams.get('seasonal_menu_id');
  const isSpecialMenu = !!seasonalMenuId;

  // Restaurant info from search results or location state
  const restaurant = location.state?.restaurant_info || location.state?.restaurant;
  const restaurantName = location.state?.restaurant_name || restaurant?.name || '';
  const restaurantLocation = location.state?.location || '';

  // Fixed restaurant info - no editing
  const restaurantInfo = {
    name: restaurantName,
    address: restaurant?.address || restaurantLocation || '',
    phone: restaurant?.phone || '',
    website: restaurant?.website || '',
    rating: restaurant?.rating || null,
    cuisine_type: restaurant?.cuisine_type || ''  // Don't use types[0] as it's just "restaurant"
  };
  
  useEffect(() => {
    // This page can work with or without restaurant context
    // With context: from frictionless search flow (restaurant data pre-filled)
    // Without context: manual upload from dashboard or other sources
    
    // If no restaurant data, try to get it from auth context
    if (!restaurant || !restaurant.name) {
      // Check if user is logged in and has restaurant info
      const token = Cookies.get('auth_token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.restaurant_name) {
            // Restaurant name from auth token if available
            // But we don't update state since restaurantInfo is now const
          }
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
      // Don't redirect - allow manual upload without pre-filled data
    }
    
    // Clear cached extraction data that shouldn't trigger claim UI
    const isCached = sessionStorage.getItem('cached_extraction') === 'true';
    if (isCached) {
      sessionStorage.removeItem('cached_extraction');
      sessionStorage.removeItem('cached_menu_items');
      sessionStorage.removeItem('extraction_completed');
      sessionStorage.removeItem('preview_session_token');
      sessionStorage.removeItem('preview_items_extracted');
    }
    
    // Check for pending extraction from frictionless flow
    const extractionCompleted = sessionStorage.getItem('extraction_completed') === 'true';
    const itemsExtracted = sessionStorage.getItem('preview_items_extracted');
    const sessionToken = sessionStorage.getItem('preview_session_token');
    
    if (extractionCompleted && itemsExtracted && sessionToken) {
      setHasPendingExtraction(true);
      setExtractedItemsCount(parseInt(itemsExtracted));
      setCheckingSession(false);
      return;
    }
    
    checkForSessionData();
  }, []);
  
  const handleClaimExtraction = async () => {
    const sessionToken = sessionStorage.getItem('preview_session_token');
    if (!sessionToken) {
      toast({
        title: "Error",
        description: "No extraction session found. Please try extracting again.",
        variant: "destructive"
      });
      return;
    }
    
    setIsClaiming(true);
    
    try {
      const response = await fetch(getApiUrl('frictionless-onboarding/claim-extraction'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_token: sessionToken })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to claim extraction');
      }
      
      const result = await response.json();
      
      // Clear session storage
      sessionStorage.removeItem('extraction_completed');
      sessionStorage.removeItem('preview_session_token');
      sessionStorage.removeItem('preview_items_extracted');
      sessionStorage.removeItem('preview_restaurant_name');
      sessionStorage.removeItem('pending_menu_extraction');
      
      toast({
        title: "Success!",
        description: `${result.items_count} menu items have been added to your restaurant.`
      });
      
      // Navigate to review page with the new menu_id
      navigate('/restaurant-menu-review', {
        state: {
          menu_id: result.menu_id,
          fromClaim: true
        }
      });
      
    } catch (error) {
      console.error('Claim error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to claim extraction. Please try again.",
        variant: "destructive"
      });
      setIsClaiming(false);
    }
  };
  
  const checkForSessionData = async () => {
    const sessionId = frictionlessTempApi.getStoredSessionId();
    const hasMenus = localStorage.getItem('frictionless_has_menus') === 'true';
    
    // Check if user explicitly wants to upload manually (from search page)
    const isManualUpload = location.state?.source === 'manual';
    
    if (sessionId && hasMenus && !isManualUpload) {
      try {
        const response = await frictionlessTempApi.getSessionData(sessionId);
        if (response.success && response.session_data?.menu_selection) {
          // Only auto-redirect if the session is recent (within last hour)
          const sessionAge = response.session_data?.created_at ? 
            Date.now() - new Date(response.session_data.created_at).getTime() : Infinity;
          
          if (sessionAge < 3600000) { // 1 hour in milliseconds
            setSessionData(response.session_data);
            
            // If we have menu selection from search, skip directly to processing
            toast({
              title: "Menu ready for processing",
              description: "We'll process your selected menu now"
            });
            
            // Navigate directly to processing with the session data
            setTimeout(() => {
              navigate('/restaurant-menu-processing', {
                state: {
                  session_id: sessionId,
                  menu_data: response.session_data.menu_selection
                }
              });
            }, 1500);
            return; // Early return to prevent setting checkingSession to false
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
        // Session expired or not found, continue with normal upload
      }
    }
    
    setCheckingSession(false);
  };
  
  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dineai-blue/95 to-dineai-blue flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-dineai-yellow mx-auto mb-4" />
          <p className="text-white">Checking menu status...</p>
        </div>
      </div>
    );
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      validateAndSetFiles(Array.from(droppedFiles));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      validateAndSetFiles(Array.from(selectedFiles));
    }
  };

  const validateAndSetFiles = (fileList: File[]) => {
    setError('');
    
    // Check if it's a PDF or images
    const isPDF = fileList.length === 1 && fileList[0].type === 'application/pdf';
    const areImages = fileList.every(f => f.type.startsWith('image/'));
    
    if (!isPDF && !areImages) {
      setError('Please upload either a PDF file or up to 2 images (PNG, JPEG)');
      return;
    }
    
    // For images, limit to 2 files
    if (areImages && fileList.length > 2) {
      setError('Please select up to 2 images only');
      return;
    }
    
    // Check file types and sizes
    for (const file of fileList) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError('Please upload PDF files or images (PNG, JPEG) only');
        return;
      }
      
      if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        setError(`File "${file.name}" is ${fileSizeMB}MB. Maximum allowed is ${MAX_UPLOAD_SIZE_MB}MB.`);
        return;
      }
    }
    
    setFiles(fileList);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleNext = async () => {
    if (files.length === 0) {
      setError('Please upload your menu before continuing');
      return;
    }
    
    setError('');
    setIsProcessing(true); // Show loading state
    
    try {
      // Always use frictionless flow for menu upload during signup
      // This ensures deduplication works and users don't get confusing errors
      const token = Cookies.get('auth_token');
      
      // Check if user has a restaurant already
      let hasRestaurant = false;
      if (token) {
        try {
          const response = await fetch(getApiUrl('restaurants/profile'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const profile = await response.json();
            hasRestaurant = !!(profile.restaurant_id || profile.rest_id);
          }
        } catch (err) {
          console.log('Could not check restaurant status');
        }
      }
      
      // Use frictionless flow if no token OR if user doesn't have a restaurant yet
      if (!token || !hasRestaurant) {
        // FRICTIONLESS FLOW: Upload to temp storage with deduplication
        let sessionId = frictionlessTempApi.getStoredSessionId();
        
        // Clear any old extraction data from previous sessions
        sessionStorage.removeItem('extraction_completed');
        sessionStorage.removeItem('preview_session_token');
        sessionStorage.removeItem('preview_items_extracted');
        sessionStorage.removeItem('preview_restaurant_name');
        sessionStorage.removeItem('extraction_job_id');
        
        // Always create a fresh session for manual upload
        // This prevents reusing expired sessions
        try {
          console.log('Creating new session for manual upload...');
          const sessionResponse = await frictionlessTempApi.createSession(
            restaurantInfo.name || 'Restaurant',
            restaurantInfo.address,
            'manual_upload'
          );
          
          if (sessionResponse && sessionResponse.session_id) {
            sessionId = sessionResponse.session_id;
            frictionlessTempApi.storeSessionId(sessionId);
            console.log('New session created:', sessionId);
            
            // Store restaurant info for later retrieval during signup
            // Use the original restaurant object from location.state which has all Google data
            if (restaurant) {
              try {
                console.log('Storing restaurant info from Google:', restaurant);
                const storeResponse = await fetch(getApiUrl('frictionless-onboarding/store-restaurant-info-session'), {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    session_token: sessionId,
                    restaurant_info: restaurant  // Send the entire restaurant object from Google
                  })
                });
                
                if (storeResponse.ok) {
                  console.log('Restaurant info stored for session');
                } else {
                  console.error('Failed to store restaurant info:', await storeResponse.text());
                }
              } catch (error) {
                console.error('Failed to store restaurant info:', error);
              }
            }
          } else {
            throw new Error('Failed to create session');
          }
        } catch (error) {
          console.error('Session creation failed:', error);
          // Clear any old session data
          frictionlessTempApi.clearStoredSession();
          localStorage.removeItem('frictionless_has_menus');
          setError('Failed to initialize upload session. Please try again.');
          setIsProcessing(false);
          return;
        }
        
        // Upload file(s) to temp storage with new session
        try {
          console.log('Uploading files to session:', sessionId);
          
          // Use batch upload for multiple files
          let uploadResult;
          if (files.length > 1) {
            // Multiple images - use batch upload endpoint
            uploadResult = await frictionlessTempApi.uploadMultipleFiles(sessionId, files);
          } else {
            // Single file (PDF or image) - use regular endpoint
            uploadResult = await frictionlessTempApi.uploadFile(sessionId, files[0]);
          }
          
          if (uploadResult.success) {
            toast({
              title: "Menu uploaded successfully!",
              description: "Processing your menu with AI..."
            });
            
            // Store session data for extraction
            sessionStorage.setItem('frictionless_session_id', sessionId);
            sessionStorage.setItem('frictionless_has_menu', 'true');
            sessionStorage.setItem('frictionless_menu_file', JSON.stringify(uploadResult.file_info));
            sessionStorage.setItem('frictionless_source', 'manual_upload');
            
            // Navigate to the SAME processing page used for search results
            // This creates our valuable menu dataset!
            navigate('/restaurant-menu-processing', {
              state: {
                session_id: sessionId,
                file_info: uploadResult.file_info,
                source: 'manual_upload',
                is_frictionless: true,
                restaurant_info: restaurantInfo,
                menus: [{
                  source_type: 'manual_upload',
                  file_info: uploadResult.file_info
                }]
              }
            });
          } else {
            throw new Error(uploadResult.error || 'Upload failed');
          }
        } catch (uploadError: any) {
          console.error('Upload failed:', uploadError);
          // Clear the failed session
          frictionlessTempApi.clearStoredSession();
          setError(uploadError.message || 'Failed to upload menu. Please try again.');
          setIsProcessing(false);
        }
        return;
      }
      
      // AUTHENTICATED FLOW: Original upload logic
      const formData = new FormData();
      
      // Backend expects a single file with parameter name 'file'
      // For now, upload the first file (we can enhance for multiple later)
      if (files.length > 0) {
        formData.append('file', files[0]);
      }
      
      // If uploading for a new location, tell the backend to use the newest location
      if (isNewLocation) {
        formData.append('use_newest_location', 'true');
        console.log('📍 Uploading menu for newly added location');
      }

      // If uploading for a special menu, include the seasonal_menu_id
      if (seasonalMenuId) {
        formData.append('seasonal_menu_id', seasonalMenuId);
        console.log('📅 Uploading special menu with ID:', seasonalMenuId);
      }

      // Upload to backend
      const response = await fetch(getApiUrl('menus/upload'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        // Handle 413 Request Entity Too Large
        if (response.status === 413) {
          setError(`File size too large. Please upload a PDF file under ${MAX_UPLOAD_SIZE_MB}MB. Try compressing your PDF or splitting it into smaller files.`);
          return;
        }
        
        // Try to parse response as JSON
        let errorMessage = 'Failed to upload menu';
        try {
          const error = await response.json();
          
          // Handle duplicate menu detection specially
          if (response.status === 409 && error.error === 'duplicate_menu') {
            const details = error.details;
            const daysText = details.days_ago === 0 ? 'today' : 
                            details.days_ago === 1 ? 'yesterday' : 
                            `${details.days_ago} days ago`;
            
            setError(`🤖 AI Detection: I've already processed this exact menu ${daysText} (${details.total_items} items found). Please upload a new menu version or different content to continue.`);
            return;
          }
          
          // Ensure errorMessage is always a string
          if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else if (typeof error.message === 'string') {
            errorMessage = error.message;
          } else if (error.detail && typeof error.detail === 'object') {
            // If detail is an object, try to extract a message from it
            errorMessage = JSON.stringify(error.detail);
          } else {
            errorMessage = errorMessage;
          }
        } catch (e) {
          // If response is not JSON (likely HTML error page)
          if (response.status === 413) {
            errorMessage = `File size too large. Please upload a PDF file under ${MAX_UPLOAD_SIZE_MB}MB.`;
          } else {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        }
        
        // Ensure errorMessage is a string before throwing
        const finalErrorMessage = typeof errorMessage === 'string' 
          ? errorMessage 
          : JSON.stringify(errorMessage);
        console.error('Menu upload failed with message:', finalErrorMessage);
        throw new Error(finalErrorMessage);
      }
      
      const data = await response.json();
      console.log('Upload response:', data);

      // Check if this is a duplicate menu offer
      if (data.action === 'duplicate_menu_found' && data.offer_copy) {
        // Show duplicate menu dialog
        setDuplicateMenuFound(data);
        setUploadSuccess(false);
        return;
      }

      // Normal upload success
      setProcessingId(data.processing_id);
      sessionStorage.setItem('processingId', data.processing_id);
      setUploadSuccess(true);
    } catch (error: any) {
      console.error('Upload error:', error);
      // Better error handling to avoid [object Object]
      let errorMessage = 'Failed to upload menu';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.detail) {
        errorMessage = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
      } else if (error?.message) {
        errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error.message);
      }
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessMenu = async () => {
    if (!processingId) {
      setError('No processing ID available');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        console.error('No auth token for processing');
        setError('Authentication required');
        setIsProcessing(false);
        return;
      }
      
      const requestBody = seasonalMenuId ? { seasonal_menu_id: parseInt(seasonalMenuId) } : {};

      const response = await fetch(getApiUrl(`menus/process/${processingId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : undefined
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start processing');
      }
      
      // Navigate to auth-menu-processing page for authenticated users
      navigate('/auth-menu-processing', {
        state: {
          processingId: processingId,
          source: 'authenticated_upload',
          seasonal_menu_id: seasonalMenuId ? parseInt(seasonalMenuId) : undefined
        }
      });
    } catch (error) {
      console.error('Processing error:', error);
      setError(error instanceof Error ? error.message : 'Failed to start processing');
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate('/restaurant-dashboard');
  };
  
  const handleUploadAnother = () => {
    setFiles([]);
    setUploadSuccess(false);
    setProcessingId(null);
    setError('');
    setDuplicateMenuFound(null);
  };

  const handleAcceptDuplicateMenu = async () => {
    if (!duplicateMenuFound) return;

    setIsCopyingMenu(true);
    setError('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Call the copy menu endpoint
      const response = await fetch(getApiUrl('menus/copy-menu'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_rest_id: duplicateMenuFound.source_menu.rest_id,
          source_location_id: duplicateMenuFound.source_menu.location_id,
          seasonal_menu_id: seasonalMenuId ? parseInt(seasonalMenuId) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to copy menu');
      }

      const result = await response.json();

      toast({
        title: "✅ Menu copied successfully!",
        description: `${result.items_copied} items have been copied to your new location.`
      });

      // Navigate to menu review page
      if (seasonalMenuId) {
        navigate('/seasonal-menu-review', {
          state: { seasonal_menu_id: parseInt(seasonalMenuId) }
        });
      } else {
        navigate('/restaurant-menu-review');
      }
    } catch (error) {
      console.error('Error copying menu:', error);
      setError(error instanceof Error ? error.message : 'Failed to copy menu');
    } finally {
      setIsCopyingMenu(false);
    }
  };

  const handleRejectDuplicateMenu = () => {
    // Clear the duplicate menu state and allow user to upload a different menu
    setDuplicateMenuFound(null);
    setFiles([]);
    setUploadSuccess(false);
    setError('');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return Math.round(bytes / 1048576) + ' MB';
  };

  // Show claim UI if there's a pending extraction
  if (hasPendingExtraction) {
    return (
      <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
        </div>

        {/* Logo at top-left corner */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute top-8 left-8 z-20"
        >
          <Logo className="text-white h-8" />
        </motion.div>

        <div className="container mx-auto px-4 flex items-center justify-center min-h-screen relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-2xl"
          >

            {/* Claim Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20 shadow-xl">
              <div className="text-center">
                {/* Success Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Package className="w-10 h-10 text-green-400" />
                </motion.div>

                <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Your Menu is Ready!
                </h1>
                
                <p className="text-white/80 text-lg mb-2">
                  We've already extracted your menu
                </p>
                
                <div className="bg-white/5 rounded-xl p-6 mb-8">
                  <p className="text-4xl font-bold text-dineai-yellow mb-2">
                    {extractedItemsCount}
                  </p>
                  <p className="text-white/60">
                    menu items extracted and ready to add
                  </p>
                </div>

                <p className="text-white/70 text-sm mb-8">
                  Click below to add these items to your restaurant and proceed to review
                </p>

                <Button
                  onClick={handleClaimExtraction}
                  disabled={isClaiming}
                  className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                  {isClaiming ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Adding Menu Items...
                    </>
                  ) : (
                    <>
                      Add Menu to Restaurant
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
        <motion.div 
          className="h-full bg-dineai-yellow"
          initial={{ width: '0%' }}
          animate={{ width: uploadSuccess ? '100%' : files.length > 0 ? '50%' : '0%' }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Logo at top-left corner */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="absolute top-8 left-8 z-20"
      >
        <Logo className="text-white h-8" />
      </motion.div>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >

          {/* Restaurant Info Card - Display only, no editing */}
          {restaurantInfo.name && (
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-6 mb-6">
              {/* Header with restaurant name and cuisine badge */}
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">{restaurantInfo.name}</h2>
                {restaurantInfo.cuisine_type && restaurantInfo.cuisine_type !== 'restaurant' && (
                  <span className="bg-dineai-yellow/20 text-dineai-yellow text-xs font-medium px-2 py-1 rounded-full capitalize">
                    {restaurantInfo.cuisine_type.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
              
              {/* Restaurant Details */}
              <div className="space-y-3">
                {restaurantInfo.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-white/70 mt-0.5" />
                    <p className="text-white/80 text-sm">{restaurantInfo.address}</p>
                  </div>
                )}
                
                {restaurantInfo.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-white/70" />
                    <p className="text-white/80 text-sm">{restaurantInfo.phone}</p>
                  </div>
                )}
                
                {restaurantInfo.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-white/70" />
                    <p className="text-white/80 text-sm">{restaurantInfo.website}</p>
                  </div>
                )}
                
                {restaurantInfo.rating && (
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-dineai-yellow fill-current" />
                    <p className="text-white/80 text-sm">{restaurantInfo.rating} stars</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Upload Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20 shadow-xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-xs flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  {uploadSuccess ? 'READY TO PROCESS' : 'MENU UPLOAD'}
                </span>
              </div>
              
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {uploadSuccess ? (
                  <>
                    Process Your{" "}
                    <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                      {isSpecialMenu ? "Special Menu" : "Menu"}
                    </span>
                  </>
                ) : (
                  <>
                    Upload Your{" "}
                    <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                      {isSpecialMenu ? "Special Menu" : "Menu"}
                    </span>
                  </>
                )}
              </h1>
              <p className="text-white/80 text-base">
                {uploadSuccess 
                  ? 'Your menu is uploaded and ready for AI processing'
                  : 'Share your menu PDF and let our AI work its magic'
                }
              </p>
            </div>

            {/* Location Selector removed - context is managed by dashboard */}

            {/* Upload Area - only show if not uploaded successfully */}
            {!uploadSuccess && (
              <div className="mb-6">
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                className={`
                  relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer
                  transition-all duration-200
                  ${isDragging 
                    ? 'border-dineai-yellow bg-dineai-yellow/10' 
                    : 'border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10'
                  }
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  multiple
                  className="hidden"
                />
                
                {files.length === 0 ? (
                  <>
                    <Upload className="w-16 h-16 text-dineai-yellow mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {isDragging ? 'Drop your menu here' : 'Drop your menu here'}
                    </h3>
                    <p className="text-white/60 mb-4">
                      Upload a PDF or up to 2 images • Click to browse from your computer
                    </p>
                    <p className="text-white/40 text-sm">
                      PDF, PNG, JPEG files • Maximum {MAX_UPLOAD_SIZE_MB}MB
                    </p>
                  </>
                ) : (
                  <>
                    {files.length === 1 && files[0].type === 'application/pdf' ? (
                      // Single PDF display
                      <>
                        <FileText className="w-16 h-16 text-dineai-yellow mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {files[0].name}
                        </h3>
                        <p className="text-white/60 mb-4">
                          {formatFileSize(files[0].size)}
                        </p>
                      </>
                    ) : (
                      // Multiple images display
                      <>
                        <ImageIcon className="w-16 h-16 text-dineai-yellow mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {files.length} image{files.length > 1 ? 's' : ''} selected
                        </h3>
                        <div className="text-white/60 mb-4 space-y-1">
                          {files.map((f, idx) => (
                            <p key={idx} className="text-sm">
                              {f.name} ({formatFileSize(f.size)})
                            </p>
                          ))}
                        </div>
                      </>
                    )}
                    <Button
                      className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFiles([]);
                        setError('');
                      }}
                    >
                      Remove {files.length > 1 ? 'Files' : 'File'}
                    </Button>
                  </>
                )}
              </div>
              
              {/* Error Message */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 p-4 rounded-xl border ${
                    error.includes('🤖 AI Detection') 
                      ? 'bg-dineai-yellow/10 border-dineai-yellow/30 text-dineai-yellow' 
                      : 'bg-red-500/10 border-red-500/30 text-red-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {error.includes('🤖 AI Detection') ? (
                      <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium leading-relaxed">
                        {error}
                      </p>
                      {error.includes('🤖 AI Detection') && (
                        <div className="mt-3">
                          <p className="text-xs mb-3 opacity-80">
                            Tip: Try uploading a different menu version, updated prices, or new seasonal items.
                          </p>
                          <Button
                            onClick={() => {
                              setFiles([]);
                              setError('');
                            }}
                            className="bg-dineai-yellow/20 text-dineai-yellow hover:bg-dineai-yellow/30 border border-dineai-yellow/30 text-xs py-2 px-4"
                          >
                            Clear & Upload New Menu
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
            )}

            {/* Alternative Option - show only when not uploaded */}

            {/* Action Buttons */}
            {!uploadSuccess ? (
              <div className="flex justify-between">
                <Button 
                  onClick={handleBack}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 py-3 px-6 text-base font-medium rounded-full flex items-center gap-2 transition-all"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </Button>
                
                <Button 
                  onClick={handleNext}
                  disabled={files.length === 0 || isProcessing}
                  className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-3 px-8 text-base font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Upload Menu
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-6"
                >
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Upload Successful!
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    Your menu has been uploaded and is ready for AI processing.
                  </p>
                </motion.div>
                
                <div className="flex gap-4 justify-center">
                  <Button 
                    onClick={handleUploadAnother}
                    className="bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 py-3 px-6 text-base font-medium rounded-full"
                  >
                    Upload Different Menu
                  </Button>
                  
                  <Button 
                    onClick={handleProcessMenu}
                    disabled={isProcessing}
                    className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-3 px-8 text-base font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        Process Menu
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Help text */}
          <p className="text-center text-white/60 text-sm mt-6">
            Your menu will be analyzed by AI to extract dishes, prices, and descriptions
          </p>
        </motion.div>
      </div>

      {/* Duplicate Menu Modal */}
      {duplicateMenuFound && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-dineai-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-dineai-yellow" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Menu Already Processed!
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {duplicateMenuFound.ai_message}
              </p>
            </div>

            {/* Menu Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Location:</span>
                  <span className="font-medium text-gray-900">
                    {duplicateMenuFound.source_menu.location_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Items Found:</span>
                  <span className="font-medium text-gray-900">
                    {duplicateMenuFound.source_menu.total_items} menu items
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Processed:</span>
                  <span className="font-medium text-gray-900">
                    {duplicateMenuFound.source_menu.days_ago === 0 ? 'Today' :
                     duplicateMenuFound.source_menu.days_ago === 1 ? 'Yesterday' :
                     `${duplicateMenuFound.source_menu.days_ago} days ago`}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={handleAcceptDuplicateMenu}
                disabled={isCopyingMenu}
                className="w-full bg-dineai-blue text-white hover:bg-dineai-blue/90 py-3 rounded-lg font-semibold transition-colors"
              >
                {isCopyingMenu ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Copying menu...
                  </>
                ) : (
                  'Yes, use this menu for this location'
                )}
              </Button>
              <Button
                onClick={handleRejectDuplicateMenu}
                disabled={isCopyingMenu}
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 py-3 rounded-lg font-semibold"
              >
                No, I want to upload a different menu
              </Button>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RestaurantMenuUpload;